import { ColorsSet } from "app/common/utils/ColorsSet";
import { camelCase, capitalize } from "lodash";

import {
  FIELDS_ATTRIBUTES_OBJ,
  FIELD_ATTRIBUTES_BY_FIELD_TYPE,
  FIELD_TYPES,
  TASK_NAMES,
} from "./constants";

export const prepareField = (fieldAttributes, values, { fieldsAttributes }) => {
  if (!fieldAttributes) return {};

  const props = {};

  // loop through all properties of fieldAttributes
  for (let key in fieldAttributes) {
    // get the attributes name
    const attr = fieldsAttributes[key];

    // if attribute is not defined
    if (!attr) continue;

    // If values has a value to be updated, then use that
    // TODO: Check data type of the attribute also
    if (typeof values[attr] !== "undefined") {
      // Take props from values\
      props[attr] = values[attr];
    } else {
      // Otherwise, attach default properties
      props[attr] = fieldAttributes[key];
    }
  }

  return props;
};
/**
 *
 * @param {{field_type_id:number; field_name:string; list_columns:any[]; is_mandatory: boolean; }[]} fields
 * @param {*} definitions
 * @returns
 */
export const prepareFields = (
  fields,
  { fieldsAttributes, fieldAttributesByFieldType, fieldTypesToSkip = {} }
) => {
  const processedFields = fields
    .filter((field) => !fieldTypesToSkip[field.field_type_id])
    .map((field) => {
      let {
        field_type_id,
        field_name,
        list_columns,
        attributes = {},
        ...rest
      } = field;
      
      attributes = typeof attributes === "object" ? attributes : {};

      if (field_type_id == 16 || field_type_id == 4) {
        attributes.format = "M j, Y g:iA";
        rest.format = "M j, Y g:iA";
      }

      const processed = prepareField(
        fieldAttributesByFieldType[field_type_id],
        { ...rest, ...attributes },
        { fieldsAttributes }
      );
      const res = {
        column_type_id: field_type_id,
        column_name: field_name,
        options: processed,
      };
      if(field_type_id == 10){
        res.is_deleted = false;
      }
      if (list_columns) {
        res.list_columns = list_columns;
      }
      return res;
    });

  return processedFields;
};

export const validateFieldSchema = (fields) => {
  return fields.map((field) => {
    if (field.options.is_unique) {
      field.options.unique_options = {
        action: "warning",
        message: `Duplicate values for ${field.column_name}. Are you sure you want to proceed?`,
      };
    }

    return field;
  });
};

export const addColorsToFields = (fields, { fieldTypes }) => {
  return fields.map((field) => {
    if (
      [
        fieldTypes.dropdown,
        fieldTypes.multiselect,
        fieldTypes.checklist,
      ].includes(field.column_type_id)
    ) {
      field.options.colors = ColorsSet.slice(
        0,
        field.options.options?.length || 0
      );
    }

    return field;
  });
};

/**
 *
 * @param {{ fields:any[] sheetName } } param0
 * @param {{fieldTypes:any; fieldsAttributes: any; fieldAttributesByFieldType:any;fieldTypesToSkip?:any;}} param1
 * @returns {{list_name:string; add_button_text:string; columns:any[]}}
 */
export const prepareSheetSchema = (
  { fields, sheetName },
  {
    fieldTypes = FIELD_TYPES,
    fieldsAttributes = FIELDS_ATTRIBUTES_OBJ,
    fieldAttributesByFieldType = FIELD_ATTRIBUTES_BY_FIELD_TYPE,
    fieldTypesToSkip = {},
  } = {
    fieldTypes: FIELD_TYPES,
    fieldsAttributes: FIELDS_ATTRIBUTES_OBJ,
    fieldAttributesByFieldType: FIELD_ATTRIBUTES_BY_FIELD_TYPE,
    fieldTypesToSkip: {},
  }
) => {
  const preparedFields = prepareFields(fields, {
    fieldsAttributes,
    fieldAttributesByFieldType,
    fieldTypesToSkip,
  });

  const validatedFields = validateFieldSchema(preparedFields);
  const columns = addColorsToFields(validatedFields, { fieldTypes });
  return { list_name: sheetName, add_button_text: `New ${sheetName}`, columns };
};

export const debounce = (func, timeOut = 500) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args);
    }, timeOut);
  };
};

export const cx = (...params) => {
  let str = "";

  for (let param of params) {
    if (!Boolean(param)) continue;

    if (typeof param === "object") {
      for (const key in param) {
        if (!Boolean(param[key])) continue;
        str += `${key} `;
      }
    } else {
      str += `${param} `;
    }
  }
  return str;
};

export const generateUUID = (() => {
  let counter = 0;
  return (length = 6) => {
    const timestamp = Date.now().toString(36);
    const uniqueID = `${timestamp}${counter.toString(36).padStart(1, "0")}`;

    // Increment the counter to ensure uniqueness for the next millisecond
    counter = (counter + 1) % 36; // 36 is the number of possible 1-digit base 36 numbers.

    return uniqueID.slice(-1 * length); // Return the last 4 characters for a 4-character ID
  };
})();

export const keyBy = (array, keyExtractor) => {
  const keyByObj = {};
  for (const item of array) {
    const key =
      typeof keyExtractor === "function"
        ? keyExtractor(item)
        : item[keyExtractor];
    keyByObj[key] = item;
  }
  return keyByObj;
};

export const getOpenAiApiKeyFromStorage = (keyName) => {
  const hashedKey = localStorage.getItem(keyName) || "";
  return hashedKey;
};

export const getValueFromLocalStorage = (key) => {
  const item = localStorage.getItem(key) || "";
  return item;
};

export const setOpenAiApiKeyFromStorage = (keyName, value) => {
  localStorage.setItem(keyName, value);
};

export const setKeyValueInLocalStorage = (key, value) => {
  localStorage.setItem(key, value);
};

export const removeOpenAiApiKeyFromStorage = (keyName) => {
  localStorage.removeItem(keyName);
};

export const isTrue = (param) => {
  if (param === "0") return false;
  return param === "1" || Boolean(param);
};

export const prepareChatMessage = (message) => {
  if (typeof message === "object") return message;
  return { type: "message", content: message, role: "user" };
};

/**
 * base64 URL decode
 * @param {*} encodedString
 * @returns {string}
 */
export const base64UrlDecode = (encodedString) => {
  // Convert URL-encoded characters back to their original form
  let cleanEncodedString = encodedString.replace(/-/g, "+").replace(/_/g, "/");

  // Add padding if necessary
  const padding = cleanEncodedString.length % 4;
  if (padding === 1) {
    throw new Error(
      "Invalid input: Length of base64 data is not a multiple of 4"
    );
  }
  if (padding === 2) {
    cleanEncodedString += "==";
  } else if (padding === 3) {
    cleanEncodedString += "=";
  }

  // Decode the Base64 string
  const decodedString = atob(cleanEncodedString);

  return decodedString;
};

export function removeQuotes(str) {
  return str.replace(/^['"]+|['"]+$/g, "");
}

export function makeKey(str) {
  return camelCase(str);
}

export function camelToNormal(camelCaseStr) {
  // Add a space before each capital letter (except the first one)
  return capitalize(camelCaseStr.replace(/([A-Z])/g, " $1"));
}

export function getHeaders(headers) {
  return Object.fromEntries(headers.entries());
}

export function fitResponseToFunctions(input, functions = {}) {
  const { sheets = [] } = input;
  for (const sheet of sheets) {
    const { parentSheetName, sheetName, fields = [], ...restParams } = sheet;

    const generalFields = [];
    const lookupFields = [];
    const formulaFields = [];
    const subsheetFields = [];

    for (const field of fields) {
      const { field_type_id } = field;
      // lookup
      if (field_type_id === 10) {
        lookupFields.push(field);
        // formula
      } else if (field_type_id === 19) {
        formulaFields.push(field);
        // subsheet
      } else if (field_type_id === 6) {
        subsheetFields.push(field);
      } else {
        generalFields.push(field);
      }
    }

    if (parentSheetName) {
      if (!functions[TASK_NAMES.createSubsheets]) {
        functions[TASK_NAMES.createSubsheets] = [];
      }

      functions[TASK_NAMES.createSubsheets].push({
        sheetName,
        parentSheetName,
        ...restParams,
        fields: generalFields,
      });
    } else {
      if (!functions[TASK_NAMES.createSheets]) {
        functions[TASK_NAMES.createSheets] = [];
      }

      functions[TASK_NAMES.createSheets].push({
        sheetName,
        parentSheetName,
        ...restParams,
        fields: generalFields,
      });
    }

    if (generalFields.length) {
      if (!functions[TASK_NAMES.addGeneralFields]) {
        functions[TASK_NAMES.addGeneralFields] = [];
      }

      functions[TASK_NAMES.addGeneralFields].push({
        sheetName,
        parentSheetName,
        ...restParams,
        fields: generalFields,
      });
    }

    if (subsheetFields.length) {
      fitResponseToFunctions(
        {
          sheets: subsheetFields.map((subsheet) => {
            return {
              parentSheetName: sheetName,
              sheetName: subsheet.field_name,
              fields: [
                ...(subsheet.subsheet_fields || []),
                ...(subsheet.attributes?.subsheet_fields || []),
              ],
            };
          }),
        },
        functions
      );
    }

    if (lookupFields.length) {
      if (!functions[TASK_NAMES.addLookupFields]) {
        functions[TASK_NAMES.addLookupFields] = [];
      }

      functions[TASK_NAMES.addLookupFields].push({
        sheetName,
        parentSheetName,
        ...restParams,
        fields: lookupFields,
      });
    }

    if (formulaFields.length) {
      if (!functions[TASK_NAMES.addFormulaFields]) {
        functions[TASK_NAMES.addFormulaFields] = [];
      }

      functions[TASK_NAMES.addFormulaFields].push({
        sheetName,
        parentSheetName,
        ...restParams,
        fields: formulaFields,
      });
    }
  }

  return functions;
}

export function formatList(
  list,
  { style, type } = { style: "long", type: "conjunction" }
) {
  const formatter = new Intl.ListFormat("en", {
    style,
    type,
  });
  return formatter.format(list);
}
